import React, { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export default function AdminPastPapers() {
    const [resources, setResources] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [resourceType, setResourceType] = useState('PDF');
    const [editFileUrl, setEditFileUrl] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [subject, setSubject] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [year, setYear] = useState(new Date().getFullYear().toString());
    const [level, setLevel] = useState('');
    const [board, setBoard] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState('');
    const [toasts, setToasts] = useState<any[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [viewResource, setViewResource] = useState<any | null>(null);

    // Confirmation Modal State
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmPayload, setConfirmPayload] = useState<{ id: string; message: string } | null>(null);

    const addToast = (message: string, type = 'success') => {
        const id = Date.now() + Math.random();
        setToasts((t) => [...t, { id, message, type }]);
        setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
    };

    const RESOURCE_TYPES = [
        { label: 'PDF', icon: 'ri-file-pdf-line', color: 'text-red-500 bg-red-50' },
        { label: 'DOCX', icon: 'ri-file-word-line', color: 'text-blue-500 bg-blue-50' },
        { label: 'PPT', icon: 'ri-file-ppt-line', color: 'text-orange-500 bg-orange-50' },
        { label: 'Spreadsheet', icon: 'ri-file-excel-line', color: 'text-emerald-600 bg-emerald-50' },
        { label: 'Video', icon: 'ri-video-line', color: 'text-purple-500 bg-purple-50' },
    ];

    const fetchResources = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/pastpapers`);
            if (res.ok) {
                const data = await res.json();
                setResources(data);
            }
        } catch (error) {
            console.error('Failed to fetch past papers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // component mounts only when Past Papers subsection is active
        setLoading(true);
        fetchResources();
    }, []);

    const resetForm = () => {
        setTitle('');
        setSubject('');
        setDate(new Date().toISOString().split('T')[0]);
        setFile(null);
        setLevel('');
        setBoard('');
        setYear(new Date().getFullYear().toString());

        setDescription('');
        setTags('');
        setEditingId(null);
        setEditFileUrl(null);
        setShowAdd(false);
        setIsUploading(false);
    };

    const startEdit = (r: any) => {
        setEditingId(r._id);
        setTitle(r.title);
        setSubject(r.subject || '');
        setLevel(r.level || '');
        setBoard(r.board || '');
        setDescription(r.description || '');
        setTags(r.tags || '');
        setResourceType(r.type || resourceType);
        setDate(r.date ? r.date.split('T')[0] : new Date().toISOString().split('T')[0]);
        setYear(r.year ? String(r.year) : (r.date ? new Date(r.date).getFullYear().toString() : new Date().getFullYear().toString()));
        setShowAdd(true);
        setEditFileUrl(`${API_BASE}/api/pastpapers/proxy/${r._id}`);
    };

    const openView = (r: any) => {
        setViewResource(r);
        setViewOpen(true);
    };

    const closeView = () => {
        setViewOpen(false);
        setViewResource(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title) return;

        setIsUploading(true);
        try {
            const token = localStorage.getItem('token');
            let res;

            if (editingId) {
                res = await fetch(`${API_BASE}/api/pastpapers/${editingId}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ title, subject, date, year, level, board, description, tags, resourceType })
                });
            } else {
                if (!file) {
                    addToast('Please select a file to upload', 'error');
                    setIsUploading(false);
                    return;
                }
                const formData = new FormData();
                formData.append('file', file);
                formData.append('title', title);
                formData.append('resourceType', resourceType);
                formData.append('subject', subject);
                formData.append('year', year);
                formData.append('date', date);
                formData.append('level', level);
                formData.append('board', board);
                formData.append('description', description);
                formData.append('tags', tags);
                formData.append('uploadedBy', 'Admin');

                res = await fetch(`${API_BASE}/api/pastpapers`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData,
                });
            }

            if (res.ok) {
                const data = await res.json();
                if (editingId) {
                    setResources(resources.map(r => r._id === editingId ? data : r));
                } else {
                    setResources([data, ...resources]);
                }
                addToast(editingId ? 'Past paper updated successfully!' : 'Past paper uploaded successfully!');
                resetForm();
            } else {
                const errorData = await res.json();
                addToast(errorData.error || 'Operation failed', 'error');
            }
        } catch (error) {
            console.error('Submit error:', error);
            addToast('Error saving past paper', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteClick = (id: string, title: string) => {
        setConfirmPayload({ id, message: `Are you sure you want to delete "${title}"? This will permanently remove the file from the cloud.` });
        setConfirmOpen(true);
    };

    const [isDeleting, setIsDeleting] = useState(false);

    const confirmDelete = async () => {
        if (!confirmPayload) return;
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/pastpapers/${confirmPayload.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setResources((prev) => prev.filter((r) => r._id !== confirmPayload.id));
                addToast('Past paper deleted successfully');
            }
        } catch (error) {
            console.error('Failed to delete past paper:', error);
            addToast('Failed to delete past paper', 'error');
        } finally {
            setIsDeleting(false);
            setConfirmOpen(false);
            setConfirmPayload(null);
        }
    };

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterBoard, setFilterBoard] = useState('');
    const [filterLevel, setFilterLevel] = useState('');

    // Logic for filters
    const availableYears = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
    const availableLevels = ['GCSE', 'A/L', 'Other'];
    const availableBoards = ['AQA', 'Edexcel', 'OCR', 'WJEC', 'CCEA', 'Other'];

    const formatDate = (d: string | undefined) => {
        if (!d) return '-';
        try { return new Date(d).toLocaleDateString('en-GB'); } catch (e) { return d; }
    };

    // Filtering logic
    const filteredResources = resources.filter(r => {
        const matchesSearch = (r.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            ((r.subject || '').toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesBoard = !filterBoard || ((r.board || '').toLowerCase() === filterBoard.toLowerCase());
        const matchesLevel = !filterLevel || ((r.level || '').toLowerCase() === filterLevel.toLowerCase());
        return matchesSearch && matchesBoard && matchesLevel;
    });

    if (loading) {
        return <div className="text-center py-12 text-slate-400">Loading past papers...</div>;
    }

    return (
        <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Past Papers</h2>
                    <p className="text-slate-500 text-sm">Manage Past Papers on the platform</p>
                </div>
                <button onClick={() => { editingId ? resetForm() : setShowAdd(!showAdd) }} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    <i className={(showAdd || editingId) ? "ri-close-line" : "ri-upload-2-line"}></i> {(showAdd || editingId) ? 'Cancel' : 'Upload Past Paper'}
                </button>
            </div>

            {/* Toast container */}
            <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
                {toasts.map((t) => (
                    <div key={t.id} className={`min-w-[200px] px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg animate-in slide-in-from-right-full duration-300 ${t.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
                        <i className={t.type === 'error' ? "ri-error-warning-line" : "ri-check-line"} /> {t.message}
                    </div>
                ))}
            </div>

            {/* Confirm modal */}
            {confirmOpen && confirmPayload && (
                <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="w-12 h-12 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-4 text-2xl">
                            <i className="ri-delete-bin-line"></i>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Past Paper?</h3>
                        <p className="text-sm text-slate-500 mb-6 leading-relaxed">{confirmPayload.message}</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-semibold hover:bg-slate-200 transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors cursor-pointer shadow-sm shadow-red-200 flex items-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? (
                                    <>
                                        <i className="ri-loader-4-line animate-spin"></i>
                                        Deleting...
                                    </>
                                ) : (
                                    'Yes, Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View modal (read-only) */}
            {viewOpen && viewResource && (
                <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
                    <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg">
                                    <i className="ri-eye-line text-emerald-700 text-lg"></i>
                                </div>
                                <div>
                                    <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: "'Inter', sans-serif" }}>{viewResource.title}</h2>
                                    <p className="text-xs text-slate-400">{viewResource.subject || ''}</p>
                                </div>
                            </div>
                            <button onClick={closeView} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
                                <i className="ri-close-line text-lg"></i>
                            </button>
                        </div>

                        <div className="px-6 py-5 flex flex-col gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <p className="text-sm font-bold text-slate-800">{viewResource.title}</p>
                                    <p className="text-xs text-slate-500 mt-1">{viewResource.subject || ''}</p>

                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="text-xs bg-slate-100 px-2 py-1 rounded">{viewResource.type || ''}</span>
                                        {viewResource.level && <span className="text-xs bg-slate-100 px-2 py-1 rounded">{viewResource.level}</span>}
                                        {viewResource.board && <span className="text-xs bg-slate-100 px-2 py-1 rounded">{viewResource.board}</span>}
                                        {viewResource.year && <span className="text-xs bg-slate-100 px-2 py-1 rounded">{viewResource.year}</span>}
                                        {viewResource.size && <span className="text-xs text-slate-500 ml-2">{viewResource.size}</span>}
                                    </div>

                                    {viewResource.tags && (
                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                            {viewResource.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean).map((tag: any) => (
                                                <span key={tag} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">#{tag}</span>
                                            ))}
                                        </div>
                                    )}

                                    {viewResource.description && (
                                        <div className="mt-4 text-sm text-slate-600">
                                            <h4 className="font-semibold text-slate-700 text-sm mb-1">Description</h4>
                                            <p>{viewResource.description}</p>
                                        </div>
                                    )}

                                    <div className="mt-4 text-xs text-slate-500">
                                        <div>Uploaded By: {viewResource.uploadedBy || 'Admin'}</div>
                                        <div>Date: {new Date(viewResource.date || viewResource.createdAt).toLocaleDateString('en-GB')}</div>
                                    </div>

                                    <div className="mt-4 flex gap-3">
                                        <a href={`${API_BASE}/api/pastpapers/proxy/${viewResource._id}`} target="_blank" rel="noopener noreferrer" className="text-sm px-3 py-2 rounded-lg bg-emerald-700 text-white">Open file</a>
                                        <button onClick={closeView} className="px-3 py-2 rounded-lg border">Close</button>
                                    </div>
                                </div>

                                <div className="md:col-span-1">
                                    <div className="border rounded p-2 bg-slate-50 h-full">
                                        <iframe src={`${API_BASE}/api/pastpapers/proxy/${viewResource._id}`} title="Past Paper preview" className="w-full h-72 border" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {(showAdd || editingId) && (
                <div className="bg-white rounded-xl border border-emerald-200 p-5 shadow-sm">
                    <h3 className="font-bold text-slate-800 text-sm mb-4">{editingId ? 'Edit Past Paper' : 'Upload New Past Paper'}</h3>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                            <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. GCSE Computer Science Past Paper 2023" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
                            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Computer Science" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Level</label>
                            <select value={level} onChange={(e) => setLevel(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                                <option value="">Select Level</option>
                                {availableLevels.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Exam Board</label>
                            <select value={board} onChange={(e) => setBoard(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                                <option value="">Select Board</option>
                                {availableBoards.map(bd => <option key={bd} value={bd}>{bd}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Year</label>
                            <input type="number" min="1900" max="2100" value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2023" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Format</label>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                {RESOURCE_TYPES.map((type) => (
                                    <button
                                        key={type.label}
                                        type="button"
                                        onClick={() => setResourceType(type.label)}
                                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all cursor-pointer ${resourceType === type.label
                                            ? 'border-emerald-600 bg-emerald-50'
                                            : 'border-slate-100 bg-white hover:border-slate-300'
                                            }`}
                                    >
                                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${type.color}`}>
                                            <i className={`${type.icon} text-base`} />
                                        </div>
                                        <span className={`text-xs font-semibold ${resourceType === type.label ? 'text-emerald-700' : 'text-slate-500'}`}>{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        {editingId && editFileUrl && (
                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-slate-600 mb-1">Current File</label>
                                <div className="border rounded p-2 bg-slate-50">
                                    <a href={editFileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline">Open file in new tab</a>
                                    <div className="mt-2">
                                        <iframe src={editFileUrl} title="Past Paper preview" className="w-full h-64 border" />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description of the resource" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 h-24" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Tags (comma separated)</label>
                            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. algebra, number-theory" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>
                        {!editingId && (
                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-slate-600 mb-1">Select File</label>
                                <input required type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer" />
                            </div>
                        )}
                        <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                            <button type="button" onClick={resetForm} disabled={isUploading} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-50">Cancel</button>
                            <button
                                type="submit"
                                disabled={isUploading}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-6 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-75 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isUploading ? (
                                    <>
                                        <i className="ri-loader-4-line animate-spin"></i>
                                        {editingId ? 'Updating...' : 'Uploading...'}
                                    </>
                                ) : (
                                    editingId ? 'Update Past Paper' : 'Upload Past Paper'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Filter and Search Bar */}
            <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl border border-slate-100">
                <div className="relative flex-1 min-w-[200px]">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input
                        type="text"
                        placeholder="Search papers or subjects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 outline-none" style={{backgroundColor:'#f1f1f1'}}
                    />
                </div>


                <select
                    value={filterBoard}
                    onChange={(e) => setFilterBoard(e.target.value)}
                    className="bg-slate-50 border-none rounded-lg px-3 py-2 text-sm text-slate-600 outline-none focus:ring-2 focus:ring-emerald-500/20 min-w-[140px]"
                >
                    <option value="">All Boards</option>
                    {availableBoards.map(b => <option key={b} value={b}>{b}</option>)}
                </select>

                <select
                    value={filterLevel}
                    onChange={(e) => setFilterLevel(e.target.value)}
                    className="bg-slate-50 border-none rounded-lg px-3 py-2 text-sm text-slate-600 outline-none focus:ring-2 focus:ring-emerald-500/20 min-w-[140px]"
                >
                    <option value="">All Levels</option>
                    {availableLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>

                {(searchQuery || filterBoard || filterLevel) && (
                    <button
                        onClick={() => { setSearchQuery(''); setFilterBoard(''); setFilterLevel(''); }}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 px-2 transition-colors cursor-pointer"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-4 overflow-x-auto">
                {filteredResources.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">{resources.length === 0 ? "No past papers found. Upload your first paper!" : "No past papers match your filters."}</div>
                ) : (
                    <table className="min-w-full table-auto text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 uppercase">
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Subject</th>
                                <th className="px-4 py-3">Format</th>
                                <th className="px-4 py-3">Board</th>
                                <th className="px-4 py-3">Level</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredResources.map((r) => (
                                <tr key={r._id} className="border-t">
                                    <td className="px-4 py-3 align-top w-[110px]">{formatDate(r.date || r.createdAt)}</td>
                                    <td className="px-4 py-3 font-semibold align-top">{r.title}</td>
                                    <td className="px-4 py-3 align-top">{r.subject || '-'}</td>
                                    <td className="px-4 py-3 align-top">{r.type || '-'}</td>
                                    <td className="px-4 py-3 align-top">{r.board || '-'}</td>
                                    <td className="px-4 py-3 align-top">{r.level || '-'}</td>
                                    <td className="px-4 py-3 align-top text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => openView(r)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer" title="View">
                                                <i className="ri-eye-line"></i>
                                            </button>
                                            <button onClick={() => startEdit(r)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer" title="Edit">
                                                <i className="ri-edit-line"></i>
                                            </button>
                                            <button onClick={() => handleDeleteClick(r._id, r.title)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
